import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useModules } from '../context/ModuleContext';
import './ModuleManager.css';

const ModuleManager = () => {
  const { user } = useAuth();
  const { refreshModules } = useModules();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    if (user?.tenantId) {
      fetchModules();
    }
  }, [user]);

  const fetchModules = async () => {
    if (!user?.tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/modules/tenant/${user.tenantId}`);
      setModules(response.data.modules || []);
    } catch (error) {
      toast.error('Error fetching modules');
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = async (moduleCode, currentEnabled) => {
    if (!user?.tenantId) {
      toast.error('Tenant information not available');
      return;
    }

    try {
      const newEnabled = !currentEnabled;
      await axios.post(
        `${API_URL}/api/modules/tenant/${user.tenantId}/${moduleCode}/toggle`,
        { enabled: newEnabled }
      );
      toast.success(`Module ${newEnabled ? 'enabled' : 'disabled'} successfully`);
      
      // Refresh modules in context
      if (refreshModules) {
        refreshModules();
      }
      
      // Refresh local state
      fetchModules();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error toggling module';
      toast.error(errorMsg);
      console.error('Error toggling module:', error);
    }
  };

  // Group modules by category
  const modulesByCategory = modules.reduce((acc, module) => {
    if (!acc[module.category]) acc[module.category] = [];
    acc[module.category].push(module);
    return acc;
  }, {});

  const filteredCategories = categoryFilter === 'all' 
    ? Object.keys(modulesByCategory)
    : [categoryFilter];

  const getPlanBadgeClass = (plan) => {
    const planClasses = {
      'free': 'plan-free',
      'basic': 'plan-basic',
      'pro': 'plan-pro',
      'enterprise': 'plan-enterprise'
    };
    return planClasses[plan] || 'plan-free';
  };

  if (loading) {
    return (
      <div className="module-manager">
        <div className="loading">Loading modules...</div>
      </div>
    );
  }

  if (!user?.tenantId) {
    return (
      <div className="module-manager">
        <div className="error-message">
          <FaInfoCircle />
          <p>Tenant information not available. Please contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="module-manager">
      <div className="module-manager-header">
        <div>
          <h1>Module Manager</h1>
          <p className="subtitle">Enable or disable features for your store</p>
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="category-filter"
        >
          <option value="all">All Categories</option>
          <option value="ecommerce">Ecommerce</option>
          <option value="content">Content</option>
          <option value="marketing">Marketing</option>
          <option value="automation">Automation</option>
          <option value="analytics">Analytics</option>
        </select>
      </div>

      {filteredCategories.length === 0 ? (
        <div className="no-modules">No modules found</div>
      ) : (
        filteredCategories.map(category => (
          <div key={category} className="module-category">
            <h3 className="category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
            <div className="modules-grid">
              {modulesByCategory[category].map(module => {
                const canEnable = !module.enabled;
                const canDisable = module.enabled;
                
                return (
                  <div
                    key={module.code}
                    className={`module-card ${module.enabled ? 'enabled' : 'disabled'}`}
                  >
                    <div className="module-header">
                      <div className="module-info">
                        <h4>{module.name}</h4>
                        <p className="module-description">{module.description || 'No description available'}</p>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={module.enabled || false}
                          onChange={() => toggleModule(module.code, module.enabled)}
                          disabled={!canEnable && !canDisable}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="module-meta">
                      <span className={`plan-badge ${getPlanBadgeClass(module.requiredPlan || 'free')}`}>
                        {module.requiredPlan || 'free'} plan
                      </span>
                      {module.enabled && (
                        <span className="enabled-badge">
                          <FaCheckCircle /> Enabled
                        </span>
                      )}
                      {!module.enabled && (
                        <span className="disabled-badge">
                          <FaTimesCircle /> Disabled
                        </span>
                      )}
                    </div>

                    {module.dependencies && module.dependencies.length > 0 && (
                      <div className="dependencies">
                        <strong>Requires:</strong> {module.dependencies.join(', ')}
                      </div>
                    )}

                    {module.enabledAt && (
                      <div className="module-info-footer">
                        <small>Enabled: {new Date(module.enabledAt).toLocaleDateString()}</small>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <div className="module-manager-info">
        <FaInfoCircle />
        <p>
          <strong>Note:</strong> Some modules may require specific subscription plans. 
          If you cannot enable a module, it may require upgrading your plan. 
          Contact support for assistance.
        </p>
      </div>
    </div>
  );
};

export default ModuleManager;
